/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Monitor events", description = "Monitor event volume, detect contract violations, and alert on sync issues so you can catch problems in your event tracking setup early.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Monitor events",
        description: "Monitor event volume, detect contract violations, and alert on sync issues so you can catch problems in your event tracking setup early.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Monitor event volume",
          "depth": 0,
          "id": "monitor-event-volume"
        }, {
          "value": "Detect event violations",
          "depth": 0,
          "id": "detect-event-violations"
        }, {
          "value": "Alert on sync issues",
          "depth": 0,
          "id": "alert-on-sync-issues"
        }, {
          "value": "Review and maintain regularly",
          "depth": 0,
          "id": "review-and-maintain-regularly"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ol: "ol",
      li: "li",
      h2: "h2",
      img: "img",
      a: "a"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Monitoring and alerts help your team find, diagnose, and fix issues in your event tracking setup before they affect downstream data. This applies to any Events implementation, whether you're building fresh or migrating from another tool."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch has three features that help engineering and analytics teams manage event collection and activation:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Per-source volume graph"
        }), "\n", _jsx(_components.li, {
          children: "In-app debugger"
        }), "\n", _jsx(_components.li, {
          children: "Per-sync configurable alerts"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The examples below show how to use each one."
      }), "\n", _jsx(_components.h2, {
        id: "monitor-event-volume",
        children: "Monitor event volume"
      }), "\n", _jsx(_components.p, {
        children: "Teams can get a quick overview of event volume over time with Hightouch's built-in visualization. Each event source page shows a graph of event volume at 1, 3, and 7 day time periods."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/migration-guide/events-volume-graph.webp",
          alt: "Event volume graph on an event source page"
        })
      }), "\n", _jsx(_components.p, {
        children: "This visual representation helps you quickly identify unusual spikes or drops in event volume, which could indicate issues with data collection or unexpected changes in user behavior."
      }), "\n", _jsx(_components.p, {
        children: "Manual checks are useful during setup, but most teams should also configure automated alerts for noticeable drops in event volume."
      }), "\n", _jsx(_components.p, {
        children: "Events uses Hightouch's configurable alerting system in conjunction with event-specific triggers to send messages by email, SMS, Slack, or PagerDuty. You can configure an alert so that a team member or Slack channel is notified when event traffic drops below a particular level, so your team can investigate quickly."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/migration-guide/events-volume-triggers.webp",
          alt: "Event volume alert trigger configuration"
        })
      }), "\n", _jsx(_components.h2, {
        id: "detect-event-violations",
        children: "Detect event violations"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch also supports alerting based on your event contracts. Contracts can send invalid events to a violations table in your warehouse. That sync to the warehouse can send an alert when violations exceed a threshold in a given time period for particular ", _jsx(_components.a, {
          href: "/events/contracts/violations",
          children: "types of violations"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To understand a particular set of violations, use the ", _jsx(_components.a, {
          href: "/events/sources/overview#debugger",
          children: "debugger"
        }), " on each event source. The debugger lets you inspect individual events and their data to see why they failed schema or contract checks. This event-level view complements volume monitoring and gives teams the detail they need to fix errors."]
      }), "\n", _jsx(_components.h2, {
        id: "alert-on-sync-issues",
        children: "Alert on sync issues"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports row and sync-level alerting for reverse ETL syncs, and treats event syncs the same way. You can set up alerts for an elevated row rejection rate, such as a percentage of rows failing during a sync to your warehouse. You can also be notified when a series of consecutive syncs fails with fatal errors. Both alerts let your engineering and analytics teams investigate and fix sync errors quickly."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/migration-guide/events-sync-alerts.webp",
          alt: "Event sync alert configuration"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Some advanced alerting capabilities may require enablement for your workspace.\nIf you don't see the event-specific alert triggers described here,", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hello, I would like to request access to advanced alerting and Events monitoring.",
            linkText: "reach out to customer support"
          }), " ", "\nto enable them."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "review-and-maintain-regularly",
        children: "Review and maintain regularly"
      }), "\n", _jsx(_components.p, {
        children: "Automated monitoring and alerts catch most issues, but regular human review still matters. Review data quality monthly or quarterly, and update contracts as you add new events or properties to your tracking plan."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's monitoring and alerts help you maintain data quality and spot changes in your collection process and user behavior over time."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
